import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AuthProvider, useAuth } from './auth/AuthContext.js'
import { LANGUAGE_LABELS, setLanguage, type SupportedLanguage } from './i18n/index.js'
import { LoginPage } from './pages/LoginPage.js'
import { ChangePasswordPage } from './pages/ChangePasswordPage.js'
import { DashboardPage } from './pages/DashboardPage.js'
import { MastersPage } from './pages/MastersPage.js'
import { EnquiriesPage } from './pages/EnquiriesPage.js'
import { BookingsPage } from './pages/BookingsPage.js'
import { VehiclePlanningPage } from './pages/VehiclePlanningPage.js'
import { TripsPage } from './pages/TripsPage.js'
import { PettyCashPage } from './pages/PettyCashPage.js'
import { BillingPage } from './pages/BillingPage.js'
import { PayablesPage } from './pages/PayablesPage.js'
import { ProfitabilityPage } from './pages/ProfitabilityPage.js'
import { ReportsPage } from './pages/ReportsPage.js'
import { SettingsPage } from './pages/SettingsPage.js'
import { AdminPage } from './pages/AdminPage.js'
import { AuditPage } from './pages/AuditPage.js'
import { NotificationBell } from './components/NotificationBell.js'
import { SupportWidget } from './components/SupportWidget.js'
import { NavIcon } from './components/NavIcon.js'
import { HomePage } from './pages/HomePage.js'
import { MODULES } from './nav/modules.js'
import { NavContext, type NavParams, type View } from './nav/NavContext.js'

const VIEWS = new Set<string>(['home', 'dashboard', 'enquiries', 'bookings', 'planning', 'trips', 'petty-cash', 'billing', 'payables', 'profitability', 'reports', 'masters', 'settings', 'audit'])

/** Deep links such as /?view=reports&report=receivable_ageing (used by the WhatsApp summary). */
function initialRoute(): { view: View; params: NavParams } {
  const q = new URLSearchParams(window.location.search)
  const v = q.get('view') ?? ''
  const params: NavParams = {}
  q.forEach((val, k) => { if (k !== 'view') params[k] = val })
  return { view: VIEWS.has(v) ? (v as View) : 'home', params }
}

function AppShell() {
  const { t, i18n } = useTranslation()
  const { user, branches, isAllBranches, currentBranchId, switchBranch, logout, can } = useAuth()
  const [route, setRoute] = useState(initialRoute)
  const view = route.view
  const go = useCallback((v: View, params: NavParams = {}) => {
    setRoute({ view: v, params })
    // Drop the deep-link query once we are navigating inside the app.
    if (window.location.search) window.history.replaceState(null, '', window.location.pathname)
  }, [])
  const nav = useMemo(() => ({ go, params: route.params }), [go, route.params])
  // A non-Owner opening an Owner-only deep link just lands on the dashboard.
  const allowed = (v: View): boolean => (v === 'settings' ? can('tenant_settings.update') : v === 'audit' ? can('audit_log.read') : true)

  const visible = MODULES.filter((m) => !m.permission || can(m.permission))
  const current = MODULES.find((m) => m.key === view)
  const pageTitle = view === 'home' ? t('app.name') : view === 'audit' ? 'Audit Trail' : view === 'dashboard' ? t('dashboard.title') : current?.label ?? ''
  const pageSub = view === 'home' ? "Choose what you'd like to do" : (current?.desc ?? '')

  return (
    <NavContext.Provider value={nav}>
      <div className="tf-app">
        <aside className="tf-side">
          <div className="tf-side-brand">
            <div className="tf-auth-wordmark">
              <span className="tf-auth-t">T</span>
              <span className="tf-auth-product">Fleet</span>
            </div>
            <div className="tf-auth-divider" />
            <span className="tf-auth-category">Transport Management Software</span>
            {user?.tenant_name && <span className="tf-side-company">{user.tenant_name}</span>}
          </div>

          <nav className="tf-side-nav">
            <button type="button" className={view === 'home' ? 'tf-side-active' : ''} onClick={() => go('home')}>
              <NavIcon name="home" />
              <span>Home</span>
            </button>
            {visible.map((m) => (
              <button key={m.key} type="button" className={view === m.key ? 'tf-side-active' : ''} onClick={() => go(m.key)}>
                <NavIcon name={m.key} />
                <span>{m.key === 'dashboard' ? t('dashboard.title') : m.label}</span>
              </button>
            ))}
            <button type="button" onClick={() => void logout()}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M10 4H5v16h5M15 8l4 4-4 4M19 12H9" />
              </svg>
              <span>Log Out</span>
            </button>
          </nav>

          <div className="tf-auth-powered tf-side-powered">
            <span>POWERED BY</span>
            <img src="/powered-by-trustline.png" alt="Trustline" />
          </div>
        </aside>

        <div className="tf-content">
          <header className="tf-topbar">
            <div className="tf-topbar-title">
              <h1>{pageTitle}</h1>
              {pageSub && <p>{pageSub}</p>}
            </div>

            <div className="tf-header-actions">
              {/* Stage 3 §2.1: available at any time, not just at login. "All
                  Branches" only ever appears when the user actually has that access. */}
              {(branches.length > 0 || isAllBranches) && (
                <select
                  className="tf-branch-select"
                  value={currentBranchId ?? ''}
                  onChange={(e) => void switchBranch(e.target.value || null)}
                >
                  {isAllBranches && <option value="">{t('common.allBranches')}</option>}
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              )}

              <div className="tf-lang" role="group" aria-label={t('common.language')}>
                {(Object.keys(LANGUAGE_LABELS) as SupportedLanguage[]).map((code) => (
                  <button key={code} type="button" aria-pressed={i18n.language === code} onClick={() => setLanguage(code)}>
                    {LANGUAGE_LABELS[code]}
                  </button>
                ))}
              </div>

              <NotificationBell />
              <span className="tf-note">{user?.name}</span>
            </div>
          </header>

          <main className="tf-main">
            {view === 'home' && <HomePage />}
            {(view === 'dashboard' || (!allowed(view) && view !== 'home')) && <DashboardPage />}
            {view === 'enquiries' && <EnquiriesPage />}
            {view === 'bookings' && <BookingsPage />}
            {view === 'planning' && <VehiclePlanningPage />}
            {view === 'trips' && <TripsPage />}
            {view === 'petty-cash' && <PettyCashPage />}
            {view === 'billing' && <BillingPage />}
            {view === 'payables' && <PayablesPage />}
            {view === 'profitability' && <ProfitabilityPage />}
            {view === 'reports' && <ReportsPage />}
            {view === 'masters' && <MastersPage />}
            {view === 'settings' && allowed('settings') && <SettingsPage />}
            {view === 'audit' && allowed('audit') && <AuditPage />}
          </main>
        </div>
        <SupportWidget />
      </div>
    </NavContext.Provider>
  )
}

function AuthGate() {
  const { user, bootstrapping } = useAuth()

  if (bootstrapping) {
    return (
      <div className="tf-login-shell">
        <p className="tf-note">Loading…</p>
      </div>
    )
  }
  if (!user) return <LoginPage />
  if (user.must_reset_password) return <ChangePasswordPage />
  return <AppShell />
}

export function App() {
  // Platform admin screen (Trustline staff) lives outside the tenant login.
  if (window.location.pathname === '/admin' || window.location.pathname.startsWith('/admin/')) return <AdminPage />
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  )
}
